import {
    BeforeSendEvent, ProgressEvent, UploadAbortedEvent,
    UploadErrorEvent, UploadStartedEvent, UploadedEvent
} from "devextreme/ui/file_uploader";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { ApiErrorData } from "../../../../../api/v2/ApiUtils";
import { generateUrl, getAccessToken } from "../../../../../api/v2/ApiUtils";
import { abandonUpload, getUploadOutcome } from "../../../../../api/v2/FilesApi";
import { CommentType } from "../../../../../dtos/CommentType";
import { StepDto } from "../../../../../dtos/StepDto";
import { UploadOutcomeState } from "../../../../../dtos/UploadOutcomeState";
import { useAppContext } from "../../../AppContext";
import { useTaskListContext } from "../../context/TaskListContext";

type Chunk = { segmentSize: number; bytesLoaded: number; bytesTotal: number };

export const SAVING_MESSAGE = "Saving to SharePoint…";
export const FORWARDING_MESSAGE = "Uploading to SharePoint…";
// Shown once every byte has left the browser: the last chunk is answered only after SharePoint
// closes the upload session and the file is recorded, which on a large file is a visible wait
// with the bar sitting just short of full.
export const FINISHING_MESSAGE = "Finishing in SharePoint…";
export const UPLOAD_ID_HEADER = "X-Upload-Id";

// With streaming on, the last chunk is acknowledged by the server within 5 seconds.
// If SharePoint needs longer to assemble the file (e.g. large PDFs), the server finishes
// in the background and the client polls for the outcome via awaitOutcome.
export const OUTCOME_POLL_MS = 3_000;
export const OUTCOME_POLL_LIMIT_MS = 15 * 60_000;
export const OUTCOME_UNKNOWN_MESSAGE = "The upload could not be confirmed. Refresh the page to see whether the file arrived.";
export const CONNECTION_LOST_MESSAGE = "The connection was lost during the upload.";
const NO_EXTENSION_LIMIT: string[] = [];

// A random GUID. The test browser (jsdom) has no crypto, so fall back to Math.random there.
export const newUploadId = (): string => {
    const c = (globalThis as any).crypto;
    if (typeof c?.randomUUID === "function") return c.randomUUID();
    const bytes = new Uint8Array(16);
    if (typeof c?.getRandomValues === "function") c.getRandomValues(bytes);
    else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const fileRow = (uploader: Element | null | undefined, fileName: string): Element | undefined =>
    Array.from(uploader?.querySelectorAll(".dx-fileuploader-file-container") ?? [])
        .find((el) => el.querySelector(".dx-fileuploader-file-name")?.textContent === fileName);

// Writes the status line under one file in the widget's list. The widget hides that line while
// a file uploads and only fills it again on success or failure, so it has to be shown here too;
// nothing else touches it until the upload finishes.
export function setFileStatus(uploader: Element | null | undefined, fileName: string, message: string) {
    const status = fileRow(uploader, fileName)?.querySelector(".dx-fileuploader-file-status-message") as HTMLElement | null;
    if (!status) return;
    status.textContent = message;
    status.style.display = "";
}

// Toggles the active finishing animation (spinner) on the file row.
export function setFinishingState(uploader: Element | null | undefined, fileName: string, isFinishing: boolean) {
    const row = fileRow(uploader, fileName);
    if (!row) return;
    if (isFinishing) {
        row.classList.add("dx-fileuploader-finishing");
        const status = row.querySelector(".dx-fileuploader-file-status-message") as HTMLElement | null;
        if (status) {
            status.textContent = FINISHING_MESSAGE;
            status.style.display = "";
        }
    } else {
        row.classList.remove("dx-fileuploader-finishing");
    }
}

export function useTaskFileUploadController(opts: {
    xsrfToken: string;
    stepId: string;
    fileType?: CommentType;
    // Submission (slot) uploads: route the file to a named restricted slot and constrain it to that
    // slot's extensions. Both optional - omitted for the generic (None/Request/Response) uploader.
    fileLabel?: string;
    allowedExtensions?: string[];
    // Fired after a successful upload + refresh (e.g. so a hosting dialog can close).
    onUploaded?: () => void;
}) {
    const { xsrfToken, stepId, fileType, fileLabel, allowedExtensions, onUploaded: onUploadedCb } = opts;
    const onUploadedCbRef = useRef(onUploadedCb);
    onUploadedCbRef.current = onUploadedCb;
    const app = useAppContext();
    const taskListContext = useTaskListContext();
    const mode = taskListContext.tasklistMode;
    const chunks = useRef<Chunk[]>([]);
    // One id per file, sent on every chunk so the server can tie them together and a cancel can
    // name what to clean up. With streaming on, chunks are answered once their bytes are in
    // SharePoint, so the widget's own bar is the whole story.
    const uploadIds = useRef(new Map<File, string>());
    const streaming = app.configuration?.streamingFileTransfer === true;
    // The request each file is on right now, so leaving the subtask can end it.
    const requests = useRef(new Map<File, XMLHttpRequest>());

    const forget = useCallback((file: File) => {
        uploadIds.current.delete(file);
        requests.current.delete(file);
    }, []);

    // Leaving the subtask ends whatever this uploader was sending: the widget goes with the
    // subtask, and an upload nobody can see finish is not one to keep going. Aborting the
    // request stops the widget's chunk loop; the server drops what it had.
    const onLeave = useRef({ app, streaming });
    onLeave.current = { app, streaming };
    useEffect(() => () => {
        for (const [file, request] of requests.current) {
            const id = uploadIds.current.get(file);
            uploadIds.current.delete(file);
            request.abort();
            if (onLeave.current.streaming && id) abandonUpload(id);
            onLeave.current.app.infoToast(`Upload cancelled: ${file.name}`);
        }
        requests.current.clear();
    }, []);

    const uploadUrl = useMemo(
        () => {
            let path: string;
            switch (fileType) {
                case CommentType.Response:
                    path = "response";
                    break;
                case CommentType.Request:
                    path = "request";
                    break;
                case CommentType.None:
                default:
                    path = "";
                    break;
            }

            let url = mode === "templates"
                ? generateUrl(`api/files/step/${stepId}/defaults`).toString()
                : generateUrl(`api/files/step/${stepId}${path ? `/${path}` : ""}`).toString();

            // Slot routing: pass the label on the query string (the uploader can't add form fields).
            // Only in grant mode - template defaults don't route to slots.
            if (mode !== "templates" && fileLabel) {
                url += `${url.includes("?") ? "&" : "?"}fileLabel=${encodeURIComponent(fileLabel)}`;
            }

            return url;
        },
        [stepId, fileType, mode, fileLabel]
    );

    // Normalize and stabilize allowedFileExtensions so re-renders with identical extensions do not
    // produce a new array reference. DevExtreme FileUploader invalidates and clears its DOM (wiping
    // out the progress bar mid-upload) whenever allowedFileExtensions changes reference.
    const extensionsKey = (allowedExtensions ?? [])
        .map((e) => `.${e.replace(/^\./, "").toLowerCase()}`)
        .join(",");
    const cachedExtensionsRef = useRef<string[]>(NO_EXTENSION_LIMIT);
    const cachedKeyRef = useRef<string>("");

    const allowedFileExtensions = useMemo(() => {
        if (!extensionsKey) {
            cachedKeyRef.current = "";
            cachedExtensionsRef.current = NO_EXTENSION_LIMIT;
            return NO_EXTENSION_LIMIT;
        }
        if (cachedKeyRef.current === extensionsKey) {
            return cachedExtensionsRef.current;
        }
        cachedKeyRef.current = extensionsKey;
        cachedExtensionsRef.current = extensionsKey.split(",");
        return cachedExtensionsRef.current;
    }, [extensionsKey]);

    const accept = useMemo(
        () => (allowedFileExtensions.length ? allowedFileExtensions.join(",") : "*/*"),
        [allowedFileExtensions]
    );

    const onUploadProgress = useCallback((e: ProgressEvent) => {
        chunks.current.push({ segmentSize: e.segmentSize, bytesLoaded: e.bytesLoaded, bytesTotal: e.bytesTotal });
        // With streaming off the widget's bar hits 100% when the bytes reach the app server, then
        // sits there while the server sends the file to SharePoint. Say so, or it looks stuck. With
        // streaming on the same moment is the wait for SharePoint to close the session.
        if (e.bytesLoaded >= e.bytesTotal) {
            setFileStatus(e.element, e.file.name, streaming ? FINISHING_MESSAGE : SAVING_MESSAGE);
        }
    }, [streaming]);

    const showHide = useCallback((el: Element | null, loading: boolean) => {
        if (!el) return;
        el.classList.toggle("dx-fileuploader-uploading", loading);
        const filesContainer = el.querySelector(".dx-fileuploader-files-container") as HTMLElement | null;
        const inputWrapper = el.querySelector(".dx-fileuploader-input-wrapper") as HTMLElement | null;
        if (filesContainer && inputWrapper) {
            filesContainer.style.display = loading ? "block" : "none";
            inputWrapper.style.display = loading ? "none" : "block";
        }
    }, []);

    const onUploadStarted = useCallback((e: UploadStartedEvent) => {
        showHide(e.element, true);
        chunks.current = [];
    }, [showHide]);

    // Everything that happens once a file is known to be saved: the list refreshes and the
    // widget goes back to its picker. Reached from the widget's own uploaded event, or from the
    // outcome lookup when the browser stopped waiting on the last chunk.
    const finishUpload = useCallback(async (element: HTMLElement, file: File, response?: string) => {
        setFinishingState(element, file.name, false);
        forget(file);
        showHide(element, false);
        const step = taskListContext.getSelectedStep();

        // For templates, add the uploaded file to the step's requestComment.files...only in front end
        if (mode === "templates" && step && response) {
            try {
                const uploadedFile = JSON.parse(response);
                if (!step.requestComment) {
                    step.requestComment = { files: [] } as any;
                }
                if (!step.requestComment.files) {
                    step.requestComment.files = [];
                }
                step.requestComment.files.push(uploadedFile);
                // Use the actual fileType to match how TaskFiles looks up the cache
                taskListContext.setSelectedStepFiles(fileType ?? CommentType.Request, step.requestComment.files);
            } catch (error) {
                console.error("Failed to parse uploaded file response", error);
            }
        }

        if (mode === "templates") {
            // In templates mode, update the selected step to trigger re-render
            // Call refreshFiles with skipCacheClear to reload DataSource from cache (no API call)
            if (step) {
                taskListContext.setSelectedStep({ ...step });
            }
            taskListContext.refreshFiles({ skipCacheClear: true });
        } else {
            await taskListContext.refreshSteps(step as StepDto);
            taskListContext.refreshFiles();
        }
        app.infoToast(`Upload complete: ${file.name}`);
        onUploadedCbRef.current?.();
    }, [taskListContext, app, showHide, forget, mode, fileType]);

    const failUpload = useCallback((element: HTMLElement, file: File, message: string) => {
        setFinishingState(element, file.name, false);
        forget(file);
        showHide(element, false);
        app.errorToast(`Upload error: ${file.name} - ${message}`);
    }, [app, showHide, forget]);

    // The last chunk's answer did not come. The bytes are all on the server, which finishes and
    // records the file regardless, so ask it how that went until it can say.
    const awaitOutcome = useCallback(async (element: HTMLElement, file: File, uploadId: string) => {
        setFinishingState(element, file.name, true);
        const deadline = Date.now() + OUTCOME_POLL_LIMIT_MS;

        while (Date.now() < deadline) {
            const result = await getUploadOutcome(uploadId);
            const outcome = result?.data;
            if (uploadIds.current.get(file) !== uploadId) {
                setFinishingState(element, file.name, false);
                return;
            }

            switch (outcome?.state) {
                case UploadOutcomeState.Done:
                    setFinishingState(element, file.name, false);
                    await finishUpload(element, file);
                    return;
                case UploadOutcomeState.Failed:
                    setFinishingState(element, file.name, false);
                    failUpload(element, file, outcome.message ?? "");
                    return;
                case UploadOutcomeState.Finishing:
                    setFinishingState(element, file.name, true);
                    break;
                default:
                    // Unknown, or the lookup itself failed: nothing more to learn from here.
                    setFinishingState(element, file.name, false);
                    failUpload(element, file, OUTCOME_UNKNOWN_MESSAGE);
                    return;
            }
            await new Promise((resolve) => setTimeout(resolve, OUTCOME_POLL_MS));
        }
        setFinishingState(element, file.name, false);
        failUpload(element, file, OUTCOME_UNKNOWN_MESSAGE);
    }, [finishUpload, failUpload]);

    const onUploaded = useCallback(
        (e: UploadedEvent) => {
            if (streaming) {
                let hasSavedFile = false;
                if (e.request.response) {
                    try {
                        const parsed = typeof e.request.response === "string" ? JSON.parse(e.request.response) : e.request.response;
                        if (parsed?.id) hasSavedFile = true;
                    } catch { }
                }

                if (!hasSavedFile) {
                    const id = uploadIds.current.get(e.file);
                    if (id) {
                        (e as any).message = FINISHING_MESSAGE;
                        setFinishingState(e.element, e.file.name, true);
                        void awaitOutcome(e.element, e.file, id);
                        return;
                    }
                }
            }
            setFinishingState(e.element, e.file.name, false);
            void finishUpload(e.element, e.file, e.request.response);
        },
        [streaming, awaitOutcome, finishUpload]
    );

    // A chunk before the last one lost its connection: the server has only part of the file and
    // will drop it, so say so rather than leave the bar where it stopped.
    const lostConnection = useCallback((element: HTMLElement, file: File, uploadId: string) => {
        abandonUpload(uploadId);
        failUpload(element, file, CONNECTION_LOST_MESSAGE);
    }, [failUpload]);

    const onBeforeSend = useCallback((e: BeforeSendEvent) => {
        if (!xsrfToken) throw Error("Invalid XSRF token");
        e.request.setRequestHeader("Authorization", `Bearer ${getAccessToken()}`);
        e.request.setRequestHeader("RequestVerificationToken", xsrfToken);
        let id = uploadIds.current.get(e.file);
        if (!id) {
            id = newUploadId();
            uploadIds.current.set(e.file, id);
        }
        e.request.setRequestHeader(UPLOAD_ID_HEADER, id);
        requests.current.set(e.file, e.request);

        if (!streaming) return;

        if (e.uploadInfo?.chunkIndex === 0)
            setFileStatus(e.element, e.file.name, FORWARDING_MESSAGE);

        const last = e.uploadInfo != null && e.uploadInfo.chunkIndex === e.uploadInfo.chunkCount - 1;

        // A request that ends with no HTTP status - connection dropped - is one the
        // widget says nothing about. A cancel also ends this way, but by then the id is forgotten.
        const uploadId = id;
        e.request.addEventListener("loadend", () => {
            if (e.request.status !== 0 || uploadIds.current.get(e.file) !== uploadId) return;
            if (last) void awaitOutcome(e.element, e.file, uploadId);
            else lostConnection(e.element, e.file, uploadId);
        });
    }, [xsrfToken, streaming, awaitOutcome, lostConnection]);

    const onUploadError = useCallback((e: UploadErrorEvent) => {
        setFinishingState(e.element, e.file.name, false);
        // Already seen off - an upload ended by leaving the subtask can still reach the widget's
        // error path if it was aborted before its first progress event.
        if (!uploadIds.current.has(e.file)) return;
        forget(e.file);
        showHide(e.element, false);
        let msg = e.error?.statusText;
        if (e.error?.response) {
            try {
                const parsed: ApiErrorData =
                    typeof e.error.response === "string" ? JSON.parse(e.error.response) : e.error.response;
                if (parsed?.message) msg = parsed.message;
            } catch { }
        }
        app.errorToast(`Upload error: ${e.file.name} - ${msg}`);
    }, [app, showHide, forget]);

    const onUploadAborted = useCallback((e: UploadAbortedEvent) => {
        setFinishingState(e.element, e.file.name, false);
        const id = uploadIds.current.get(e.file);
        forget(e.file);
        showHide(e.element, false);
        // With streaming on the server may hold a half-sent file for this id; let it clean up now.
        if (streaming && id) abandonUpload(id);
        app.errorToast(`Upload aborted: ${e.file.name}`);
    }, [app, showHide, forget, streaming]);

    const validateFile = useCallback((fileName: string, allowedExts?: string[]) => {
        const ext = "." + (fileName.split(".").pop() ?? "").toLowerCase();
        if (!ext || (allowedExts?.length && !allowedExts.includes(ext))) {
            return { ok: false as const, reason: `File type ${ext} is not allowed.` };
        }

        const requestFiles = taskListContext.getSelectedStepFiles(CommentType.Request) ?? [];
        const responseFiles = taskListContext.getSelectedStepFiles(CommentType.Response) ?? [];
        const noneFiles = taskListContext.getSelectedStepFiles(CommentType.None) ?? [];

        if ([...requestFiles, ...responseFiles, ...noneFiles].some((f) => f.name === fileName)) {
            return { ok: false as const, reason: `A file named ${fileName} already exists for this subtask.` };
        }

        return { ok: true as const };
    }, [taskListContext]);

    // Returns true when every selected file is valid (caller may then proceed to upload). A single
    // bad file rejects the whole selection - the uploader has no way to drop one file from a batch.
    const onValueChange = useCallback((files: File[], reset: () => void): boolean => {
        if (!files?.length) return false;

        const seen = new Set<string>();
        for (const f of files) {
            const verdict = seen.has(f.name)
                ? { ok: false as const, reason: `${f.name} was selected more than once.` }
                : validateFile(f.name, allowedFileExtensions);
            if (!verdict.ok) {
                app.errorToast(verdict.reason);
                reset();
                return false;
            }
            seen.add(f.name);
        }
        return true;
    }, [validateFile, allowedFileExtensions, app]);

    return {
        uploadUrl,
        accept,
        allowedFileExtensions,
        onBeforeSend,
        onUploadProgress,
        onUploadStarted,
        onUploaded,
        onUploadError,
        onUploadAborted,
        onValueChange
    };
}
